/**
 * Route change tracker — fires a server-side activity row every time an
 * authenticated user opens a module, tab, or significant page.
 *
 * Design notes:
 *   • Debounced per path so React double-mount in Strict Mode does not
 *     create duplicate rows.
 *   • Asks for geolocation permission ONCE per session; if the user grants
 *     it, every subsequent navigation ships lat/lng + accuracy. If the
 *     user denies, every row logs `location_status: "denied"` so
 *     auditors can see the real reason GPS is missing (never fabricated).
 *   • Does nothing on `/login`, `/complete-setup`, 404 routes, or when
 *     there is no stored token.
 */
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { BACKEND_URL } from '../config/canonical';

// Map "canonical" first-segment of the path to a readable module name.
// Anything not in this list falls through to a TitleCase of the segment.
const MODULE_MAP = {
  dashboard:          'Dashboard',
  cases:              'Cases',
  'case-operations':  'Case Operations',
  clients:            'Clients',
  'clients-hub':      'Clients Hub',
  evidence:           'Evidence',
  documents:          'Documents',
  'case-logs-hub':    'Case Logs Hub',
  'skip-trace':       'Skip Trace',
  'skip-trace-tools': 'Skip Trace Tools',
  'field-operations': 'Field Operations',
  'my-time-clock':    'My Time Clock',
  'field-map':        'Field Map',
  'field-command':    'Field Command',
  team:               'Nexus Investigators',
  'nexus-new-hire':   'Recruitment',
  recruitment:        'Recruitment',
  intel:              'Intelligence',
  'intel-map':        'Intel Map',
  ata:                'ATA Warrants',
  warrants:           'Warrants',
  admin:              'Admin',
  tips:               'Tips',
  'tip-portal':       'Tip Portal',
  reports:            'Reports',
  notifications:      'Notifications',
  profile:            'My Profile',
};

const SKIP_PATHS = new Set(['/login', '/complete-setup', '/forgot-password', '/']);
const DEDUPE_WINDOW_MS = 1500;   // ignore the exact same path within this window

// Session-scoped GPS state — initialised the first time the tracker runs.
// We never ask more than once per tab.
let _geoAsked = false;
let _geoStatus = 'unknown';   // 'granted' | 'denied' | 'unavailable' | 'unsupported'
let _geoLast = null;          // { lat, lng, accuracy, at }
const GEO_FRESH_MS = 60 * 1000;   // reuse cached coords for 60s

async function fetchGeolocation() {
  if (!navigator?.geolocation) {
    _geoStatus = 'unsupported';
    return null;
  }
  // Reuse a fresh fix if we have one — avoids a GPS warm-up delay on each nav.
  if (_geoLast && (Date.now() - _geoLast.at) < GEO_FRESH_MS) return _geoLast;
  return new Promise(resolve => {
    try {
      navigator.geolocation.getCurrentPosition(
        pos => {
          _geoStatus = 'granted';
          _geoLast = {
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
            accuracy: pos.coords.accuracy,
            at: Date.now(),
          };
          resolve(_geoLast);
        },
        err => {
          if (err.code === 1) _geoStatus = 'denied';
          else if (err.code === 2) _geoStatus = 'unavailable';
          else _geoStatus = 'error';
          resolve(null);
        },
        { enableHighAccuracy: false, timeout: 6000, maximumAge: 60000 },
      );
    } catch { _geoStatus = 'error'; resolve(null); }
  });
}

function moduleFor(pathname) {
  if (!pathname || pathname === '/') return 'Dashboard';
  const seg = pathname.split('/').filter(Boolean)[0] || 'dashboard';
  return MODULE_MAP[seg] || seg.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

export function useActivityTracker() {
  const location = useLocation();
  const lastRef = useRef({ path: null, at: 0 });
  const prevRef = useRef(null);

  useEffect(() => {
    const path = location.pathname + (location.search || '');
    if (SKIP_PATHS.has(location.pathname)) { prevRef.current = path; return; }
    const token = typeof window !== 'undefined' ? localStorage.getItem('nexus_token') : null;
    if (!token) { prevRef.current = path; return; }

    const now = Date.now();
    if (lastRef.current.path === path && (now - lastRef.current.at) < DEDUPE_WINDOW_MS) return;
    lastRef.current = { path, at: now };

    (async () => {
      // Ask for geolocation permission on the first eligible navigation.
      if (!_geoAsked) { _geoAsked = true; await fetchGeolocation(); }
      // Otherwise just reuse the cached fix (or null if denied / unavailable).
      const coords = _geoLast && (now - _geoLast.at) < GEO_FRESH_MS ? _geoLast : null;

      const body = {
        module: moduleFor(location.pathname),
        path,
        from_path: prevRef.current,
        source: 'web',
      };
      if (coords) {
        body.lat = coords.lat;
        body.lng = coords.lng;
        body.accuracy = coords.accuracy;
        body.location_source = 'browser_gps';
      } else if (_geoStatus && _geoStatus !== 'unknown') {
        body.location_source = 'browser_gps';
        body.location_status = _geoStatus;
      }
      try {
        await fetch(`${BACKEND_URL}/api/activity/navigate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify(body),
          // Keep this fire-and-forget — a navigation audit row must never
          // block the UI.
          keepalive: true,
        });
      } catch { /* swallow */ }
      prevRef.current = path;
    })();
  }, [location.pathname, location.search]);
}
